import { useEffect, useState } from 'react'
import { api, type AccountAttachmentStoreView, type AccountErpView } from '../lib/api.ts'
import { AccountAttachmentStore } from './AccountAttachmentStore.tsx'
import { AccountErpConnection } from './AccountErpConnection.tsx'

/**
 * An account's Conexão tab (#689): how its Formula Certa is reached, and
 * where its prescriptions are read from, in two sections saved apart.
 *
 * The connection is read once, here, and handed to both: the attachments
 * database is saved in the connection's own row, so a section that kept a
 * copy of its own would save the other's last change away. Each read fails on
 * its own, so a store that will not load still leaves the connection editable.
 */
export function AccountConnection({ accountId }: { accountId: string }) {
  const [erp, setErp] = useState<AccountErpView | undefined>()
  const [store, setStore] = useState<AccountAttachmentStoreView | undefined>()
  const [erpError, setErpError] = useState<string | undefined>()
  const [storeError, setStoreError] = useState<string | undefined>()

  useEffect(() => {
    const failed = (set: (message: string) => void) => (cause: unknown) =>
      set(cause instanceof Error ? cause.message : 'falha ao carregar')
    void api.accountErp(accountId).then(setErp).catch(failed(setErpError))
    void api.accountAttachmentStore(accountId).then(setStore).catch(failed(setStoreError))
  }, [accountId])

  return (
    <>
      {erpError !== undefined && <p className="text-tP text-text-error">{erpError}</p>}
      {erp !== undefined && (
        <AccountErpConnection accountId={accountId} view={erp} onSaved={setErp} />
      )}
      {storeError !== undefined && <p className="text-tP text-text-error">{storeError}</p>}
      {erp !== undefined && store !== undefined && (
        <AccountAttachmentStore
          // Begun again when the connection comes or goes, so what it typed
          // for one that was removed is not offered for the next one.
          key={erp.connection === null ? 'no-connection' : 'connection'}
          accountId={accountId}
          erpView={erp}
          storeView={store}
          onErpSaved={setErp}
          onStoreSaved={setStore}
        />
      )}
    </>
  )
}
